import { useEffect, useRef, useCallback } from 'react';

/* --------------------------------------------------------------------------
 * useNotificationsSocket — WebSocket subscription for live push notifications.
 *
 * - Opens WS to /api/ws/notifications?token=<accessToken>
 * - Passes the token from localStorage (`token` key)
 * - Auto-reconnects with exponential backoff on close
 * - Sends a `ping` every 45s to keep the connection alive across proxies
 * - Calls `onMessage(payload)` for every parsed JSON message
 *
 * Returns nothing — this is a fire-and-forget subscription that lives with
 * the mounting component.
 * ------------------------------------------------------------------------- */
export function useNotificationsSocket(onMessage) {
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const heartbeatRef = useRef(null);
  const cbRef = useRef(onMessage);
  const stoppedRef = useRef(false);

  // Keep the latest callback in a ref so reconnection doesn't need to
  // re-subscribe when the callback identity changes on every render.
  useEffect(() => { cbRef.current = onMessage; }, [onMessage]);

  const connect = useCallback(() => {
    if (stoppedRef.current) return;
    const token = localStorage.getItem('token');
    if (!token) {
      // No auth yet — retry in 5s.
      setTimeout(connect, 5000);
      return;
    }
    // Derive ws:// or wss:// from REACT_APP_BACKEND_URL (falls back to same origin).
    const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    let base;
    try {
      const u = new URL(backend);
      base = `${u.protocol === 'https:' ? 'wss:' : 'ws:'}//${u.host}`;
    } catch {
      base = window.location.origin.replace(/^http/, 'ws');
    }
    const url = `${base}/api/ws/notifications?token=${encodeURIComponent(token)}`;
    let ws;
    try {
      ws = new WebSocket(url);
    } catch {
      // Backoff and retry.
      const delay = Math.min(30_000, 1000 * 2 ** retryRef.current++);
      setTimeout(connect, delay);
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      retryRef.current = 0;
      // Heartbeat — many proxies drop idle WS after 60s.
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      heartbeatRef.current = setInterval(() => {
        try { if (ws.readyState === WebSocket.OPEN) ws.send('ping'); } catch {/* ignore */}
      }, 45_000);
    };

    ws.onmessage = (evt) => {
      if (evt.data === 'pong') return;
      try {
        const payload = JSON.parse(evt.data);
        cbRef.current && cbRef.current(payload);
      } catch {
        /* non-JSON — ignore */
      }
    };

    ws.onclose = () => {
      if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
      if (stoppedRef.current) return;
      const delay = Math.min(30_000, 1000 * 2 ** retryRef.current++);
      setTimeout(connect, delay);
    };

    ws.onerror = () => {
      // onclose will follow and handle reconnection.
      try { ws.close(); } catch {/* ignore */}
    };
  }, []);

  useEffect(() => {
    stoppedRef.current = false;
    connect();
    return () => {
      stoppedRef.current = true;
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch {/* ignore */}
      }
    };
  }, [connect]);
}
